import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import { RoundedButton } from 'components/atoms/Buttons/MainButton';
import AdvancedSearchButton from 'components/SearchFilters/AdvancedSearch/AdvancedSearchButton';
import { getAppliedJobFilterChips, hasAppliedJobFilters } from 'components/SearchFilters/appliedJobFilters';
import FilterChipList from 'components/SearchFilters/FilterChipList';
import ProcedureFilter from 'components/SearchFilters/MainFilters';
import { Permission } from 'interfaces/permissionOption';
import { Study } from 'interfaces/study';
import { every, isEmpty, isNumber, map, omit, values } from 'lodash';
import React from 'react';
import { BooleanParam, useQueryParam } from 'use-query-params';
import { allFiltersHasValue } from 'utils/helpers';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';
import useSearchFiltersFromQueryParams from '../../SearchFilters/hooks/useSearchFiltersFromQueryParams';
import { SlideJobCondition } from './DatasetSlideJobConditionRow';

const inlineJobFilterKeys = new Set(['types', 'statuses']);

export const FiltersAndExport: React.FC<
  React.PropsWithChildren<{
    totalCases: number;
    qcLabelConfig?: Study['qcLabelsConfig'];
    setIsSaveCohortModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }>
> = ({ totalCases, setIsSaveCohortModalOpen, qcLabelConfig }) => {
  const { hasPermission } = usePermissions();

  const showApprovedFilter = hasPermission(Permission.ApproveCases);

  const { queryParams, setQueryParams } = useEncodedFilters();
  const [advancedSearchDrawerOpen] = useQueryParam('advancedSearchDrawerOpen', BooleanParam);

  const { searchFiltersFromQueryParams, featuresFromQueryParams } = useSearchFiltersFromQueryParams(false);

  const onResetFilters = () => {
    setQueryParams({
      filters: { studyId: queryParams.filters?.studyId },
      features: null,
      clinicalData: null,
      queryId: null,
      searchTerm: null,
      datasetJobFilters: null,
      datasetJobFiltersIncludeExclude: null,
      page: 1,
    });
  };

  const appliedJobFilterChips = getAppliedJobFilterChips(
    queryParams.datasetJobFilters,
    queryParams.datasetJobFiltersIncludeExclude
  ).filter((chip) => !inlineJobFilterKeys.has(chip.key));
  const jobFiltersApplied = hasAppliedJobFilters(
    queryParams.datasetJobFilters,
    queryParams.datasetJobFiltersIncludeExclude
  );

  const onDeleteJobFilter = (key: string) => {
    if (key === 'jobsFiltersIncludeExclude') {
      setQueryParams({ datasetJobFiltersIncludeExclude: null, page: 1 });
      return;
    }

    const nextJobsFilters = omit(queryParams.datasetJobFilters, [key]);
    const stillApplied = hasAppliedJobFilters(nextJobsFilters, queryParams.datasetJobFiltersIncludeExclude);
    setQueryParams({
      datasetJobFilters: stillApplied ? nextJobsFilters : null,
      datasetJobFiltersIncludeExclude: stillApplied ? queryParams.datasetJobFiltersIncludeExclude : null,
      page: 1,
    });
  };

  const { studyId: filteredStudyId = undefined, ...filtersCopy } = {
    ...queryParams.filters,
    ...queryParams.features,
    ...queryParams.clinicalData,
    ...(queryParams.searchTerm ? { searchTerm: queryParams.searchTerm } : {}),
  };

  const filtersForSavingCohort = omit(filtersCopy, ['cohortIds']);

  const hasFilters = allFiltersHasValue(filtersCopy) || (filteredStudyId && filteredStudyId !== 0);

  const shouldAllowSaveCohort =
    !every(values(filtersForSavingCohort), (value: any) => !isNumber(value) && isEmpty(value)) && totalCases > 0;

  const hasSomeNonMainFilters = !isEmpty(searchFiltersFromQueryParams) || !isEmpty(featuresFromQueryParams);
  const hasClearableFilters = hasSomeNonMainFilters || jobFiltersApplied;

  return (
    <Grid item container direction="column" spacing={2} data-cy="filters-and-exports" flexWrap="nowrap">
      <Grid container item xs={12} direction="row" justifyContent="space-between" alignItems={'start'} wrap="wrap">
        {!advancedSearchDrawerOpen && (
          <Grid item sx={{ display: 'flex', justifyContent: 'start' }} mr={1}>
            <AdvancedSearchButton text="Filter Cases" isIconButton filled={hasFilters} />
          </Grid>
        )}
        <Grid item xs={true}>
          <ProcedureFilter showApprovedFilter={showApprovedFilter} isDrawer={false} />
        </Grid>
        <Grid item xs={12}>
          <SlideJobCondition />
        </Grid>
      </Grid>
      {(hasClearableFilters || shouldAllowSaveCohort) && (
        <Grid container item justifyContent="space-between" spacing={1}>
          <Grid item>
            <FilterChipList
              allFilters={searchFiltersFromQueryParams}
              allFeatures={featuresFromQueryParams}
              qcLabelConfig={qcLabelConfig}
            />
            {appliedJobFilterChips.length > 0 && (
              <Grid container item spacing={2} data-testid="applied-job-filters">
                {map(appliedJobFilterChips, (chip) => (
                  <Grid item key={chip.key}>
                    <Chip
                      label={`Cases by job: ${chip.label}`}
                      onDelete={() => onDeleteJobFilter(chip.key)}
                      data-testid={`applied-job-filter-${chip.key}`}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
          <Grid item container direction="row" spacing={1} justifyContent={'flex-end'} xs={3}>
            {hasClearableFilters && (
              <Grid item>
                <RoundedButton variant="outlined" color="secondary" onClick={onResetFilters}>
                  Clear
                </RoundedButton>
              </Grid>
            )}
            {shouldAllowSaveCohort && (
              <Grid item>
                <RoundedButton variant="contained" onClick={() => setIsSaveCohortModalOpen(true)}>
                  Save Search
                </RoundedButton>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
